
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars, Line } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { useTranslation } from 'react-i18next';
import useDreamWeaveData from '../../hooks/useDreamWeaveData';
import { collection, query, onSnapshot, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { useAppContext } from '../../AppContext';
import * as THREE from 'three';

import { isMobile } from '../../utils/isMobile';

import NeuronDetailSheet from './NeuronDetailSheet';

const Neuron = ({ position, color, size, onClick }) => {
    const meshRef = useRef();
    useFrame((state) => {
        const time = state.clock.getElapsedTime();
        const pulse = (Math.sin(time * 2) + 1) / 2;
        if(meshRef.current) {
            meshRef.current.scale.set(1 + pulse * 0.2, 1 + pulse * 0.2, 1 + pulse * 0.2);
        }
    });
    return (
        <mesh ref={meshRef} position={position} onClick={onClick}>
            <sphereGeometry args={[size, 16, 16]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.8} />
        </mesh>
    );
};

const Synapse = ({ start, end, color }) => {
    const points = useMemo(() => [new THREE.Vector3(...start), new THREE.Vector3(...end)], [start, end]);
    return <Line points={points} color={color} lineWidth={1} transparent opacity={0.3} />;
};

const Spark = ({ position }) => {
    const meshRef = useRef();
    const [lifespan, setLifespan] = useState(1);

    useFrame((_, delta) => {
        if (meshRef.current) {
            meshRef.current.scale.set(lifespan, lifespan, lifespan);
            setLifespan(l => Math.max(0, l - delta * 0.5));
        }
    });

    return (
        <mesh ref={meshRef} position={position}>
            <sphereGeometry args={[0.5, 8, 8]} />
            <meshStandardMaterial color="#FFFFFF" emissive="#FFFFFF" emissiveIntensity={2} transparent opacity={lifespan} />
        </mesh>
    );
}

const DreamWeave3D = () => {
    const { t } = useTranslation();
    const { db } = useAppContext();
    const { neurons, synapses, loading } = useDreamWeaveData('globalDreamWeave', null);
    const [sparks, setSparks] = useState([]);
    const [selectedNeuron, setSelectedNeuron] = useState(null);
    const controlsRef = useRef();
    const mobile = useMemo(() => isMobile(), []);

    useEffect(() => {
        if (!db) return;
        const sparksCollection = collection(db, `dreamWeaves/globalDreamWeave/sparks`);
        const q = query(sparksCollection, orderBy('timestamp', 'desc'), limit(10));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const newSpark = { id: change.doc.id, position: [Math.random() * 20 - 10, Math.random() * 20 - 10, Math.random() * 20 - 10] };
                    setSparks(s => [newSpark, ...s.slice(0, 19)]); // Keep max 20 sparks
                }
            });
        });

        return () => unsubscribe();
    }, [db]);

    const handleNeuronClick = (neuron) => {
        if (mobile) {
            setSelectedNeuron(neuron);
        } else {
            alert(t('dreamWeave3D.neuronInfo', { id: neuron.id, content: neuron.content }));
        }
    };

    const handleDoubleClick = () => {
        if (controlsRef.current) {
            controlsRef.current.reset();
        }
    };

    return (
        <>
            <Canvas 
                camera={{ position: [0, 0, 60], fov: 75 }} 
                onDoubleClick={handleDoubleClick}
                dpr={mobile ? 1 : 1.5} // Lower pixel ratio on mobile
                gl={{ toneMapping: THREE.NoToneMapping }}
            >
                <ambientLight intensity={0.1} />
                <pointLight position={[0, 0, 0]} color="#8A2BE2" intensity={2} distance={150} />
                <Stars 
                    radius={100} 
                    depth={50} 
                    count={mobile ? 2500 : 5000} // Fewer stars on mobile
                    factor={5} 
                    saturation={0} 
                    fade 
                />
                
                {!loading && neurons.map(neuron => (
                    <Neuron key={neuron.id} {...neuron} onClick={() => handleNeuronClick(neuron)} />
                ))}
                {!loading && synapses.map(synapse => (
                    <Synapse key={synapse.id} {...synapse} />
                ))}
                {sparks.map(spark => (
                    <Spark key={spark.id} {...spark} />
                ))}

                <EffectComposer>
                    <Bloom 
                        luminanceThreshold={0.3} 
                        luminanceSmoothing={0.9} 
                        height={mobile ? 200 : 300} // Lower resolution bloom on mobile
                        intensity={mobile ? 0.8 : 1}
                    />
                </EffectComposer>

                <OrbitControls ref={controlsRef} enableZoom={true} enablePan={true} autoRotate autoRotateSpeed={0.2} />
            </Canvas>
            <NeuronDetailSheet neuron={selectedNeuron} onClose={() => setSelectedNeuron(null)} />
        </>
    );
};

export default DreamWeave3D;
